import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function OrphanedUsersCard({ users = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [chosenUser, setChosenUser] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/orphaned-users/scan');
      setData(res.data);
      const initial = {};
      res.data.orphaned_user_ids.forEach((o) => { if (o.guessed_new_user) initial[o.old_id] = o.guessed_new_user.id; });
      setChosenUser(initial);
      setSelected({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const apply = async () => {
    setApplying(true);
    try {
      const mapping = {};
      data.orphaned_user_ids.forEach((o) => {
        if (selected[o.old_id] && chosenUser[o.old_id]) mapping[o.old_id] = chosenUser[o.old_id];
      });
      const res = await api.post('/admin/maintenance/orphaned-users/apply', { mapping });
      toast.success(`${res.data.total_docs_touched} document(s) reassigned`);
      setConfirmOpen(false);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Orphaned user references"
      description="User ids referenced across projects/estimates/jobs/notifications that no longer exist in Users (left over from an account migration). Assign each one to the current user it should point to."
      testId="orphaned-users"
      count={data ? data.total_orphaned_ids : null}
      loading={loading}
      onScan={scan}
    >
      {data && data.orphaned_user_ids.length > 0 && (
        <div className="space-y-3">
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.orphaned_user_ids.map((o) => (
              <div key={o.old_id} className="border border-gray-100 rounded-lg p-3 text-xs" data-testid={`orphaned-users-row-${o.old_id}`}>
                <div className="flex items-start gap-2">
                  <Checkbox data-testid={`orphaned-users-row-${o.old_id}-checkbox`} checked={!!selected[o.old_id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [o.old_id]: !!v }))} className="mt-1" />
                  <div className="flex-1 space-y-1.5">
                    <p className="font-semibold text-gray-800">{o.old_id.slice(0, 8)}… · {o.occurrence_count} occurrence(s)</p>
                    <p className="text-gray-500">Name hint: {o.resolved_name_hint || '(none found)'} · Collections: {Object.keys(o.by_collection).join(', ')}</p>
                    <Select value={chosenUser[o.old_id] || ''} onValueChange={(v) => setChosenUser((s) => ({ ...s, [o.old_id]: v }))}>
                      <SelectTrigger data-testid={`orphaned-users-row-${o.old_id}-select`} className="h-8 text-xs w-64">
                        <SelectValue placeholder="Assign to current user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button data-testid="orphaned-users-apply-btn" size="sm" disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
            Apply selected ({selectedCount})
          </Button>
        </div>
      )}
      {data && data.orphaned_user_ids.length === 0 && <p className="text-xs text-emerald-600">No orphaned user references found.</p>}

      <ConfirmApplyDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={apply}
        confirming={applying}
        title="Reassign orphaned users"
        summary={`This will rewrite user references on ${selectedCount} old id(s) to the assigned current user across every collection. Users themselves are never created, deleted, or password-changed.`}
      />
    </MaintCardShell>
  );
}
